import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import moment from 'moment';
import { HiOutlinePlus } from 'react-icons/hi';
import { MdClose, MdEdit, MdDelete, MdVisibility } from 'react-icons/md';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {
  getScheduledCohorts,
  createScheduledCohort,
  updateScheduledCohort,
  deleteScheduledCohort,
} from '../../../services/apiService';
import { COURSE_TYPE, COURSE_TYPE_SHORT_NAMES_MAPPING } from '../../../constants';
import { styledDefaultColDef } from '../../../extras/cellRenderers';

const STATUS_OPTIONS = ['created', 'scheduled', 'active', 'completed', 'cancelled'];
const COURSE_TYPE_OPTIONS = Object.values(COURSE_TYPE);

const STATUS_STYLE = {
  created:   { bg: '#E0F2FE', color: '#0369A1', border: '#BAE6FD' },
  scheduled: { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
  active:    { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' },
  completed: { bg: '#EDE9FE', color: '#4C1D95', border: '#C4B5FD' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' },
};

const initialForm = {
  name: '',
  courseType: COURSE_TYPE.DATA_SCIENCE,
  type: 'learning',
  startDate: null,
  classStartDate: null,
  scheduledAt: null,
};

function StatusBadge({ value }) {
  const s = STATUS_STYLE[value] || { bg: '#F3F4F6', color: '#374151', border: '#E5E7EB' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 9999, fontSize: 11.5, fontWeight: 600, backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}`, lineHeight: 1.6 }}>
        {value || '—'}
      </span>
    </div>
  );
}

export default function ScheduledCohortsPage() {
  const [rows, setRows] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCourseType, setFilterCourseType] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [isView, setIsView] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);

  const fetchData = useCallback(async () => {
    try {
      const res = await getScheduledCohorts({
        page: 1,
        limit: 100,
        status: filterStatus || undefined,
        courseType: filterCourseType || undefined,
      });
      setRows(res?.rows || []);
    } catch (_) {}
  }, [filterStatus, filterCourseType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditTarget(null);
    setIsView(false);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openView = useCallback((row) => {
    setEditTarget(row);
    setIsView(true);
    setForm({
      name: row.name || '',
      courseType: row.courseType || COURSE_TYPE.DATA_SCIENCE,
      type: row.type || 'learning',
      startDate: row.startDate ? new Date(row.startDate) : null,
      classStartDate: row.classStartDate ? new Date(row.classStartDate) : null,
      scheduledAt: row.scheduledAt ? new Date(row.scheduledAt) : null,
    });
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setEditTarget(row);
    setIsView(false);
    setForm({
      name: row.name || '',
      courseType: row.courseType || COURSE_TYPE.DATA_SCIENCE,
      type: row.type || 'learning',
      startDate: row.startDate ? new Date(row.startDate) : null,
      classStartDate: row.classStartDate ? new Date(row.classStartDate) : null,
      scheduledAt: row.scheduledAt ? new Date(row.scheduledAt) : null,
    });
    setModalOpen(true);
  }, []);

  const handleClose = () => {
    setModalOpen(false);
    setEditTarget(null);
    setIsView(false);
    setForm(initialForm);
  };

  const handleReset = () => {
    setForm(
      editTarget
        ? {
            name: editTarget.name || '',
            courseType: editTarget.courseType || COURSE_TYPE.DATA_SCIENCE,
            type: editTarget.type || 'learning',
            startDate: editTarget.startDate ? new Date(editTarget.startDate) : null,
            classStartDate: editTarget.classStartDate ? new Date(editTarget.classStartDate) : null,
            scheduledAt: editTarget.scheduledAt ? new Date(editTarget.scheduledAt) : null,
          }
        : initialForm
    );
  };

  const handleSubmit = async () => {
    if (!form.name || !form.courseType || !form.startDate || !form.classStartDate || !form.scheduledAt) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        courseType: form.courseType,
        type: form.type,
        startDate: form.startDate.toISOString(),
        classStartDate: form.classStartDate ? form.classStartDate.toISOString() : undefined,
        scheduledAt: form.scheduledAt.toISOString(),
      };
      if (editTarget) {
        await updateScheduledCohort(editTarget.id, {
          name: payload.name,
          startDate: payload.startDate,
          classStartDate: payload.classStartDate,
          scheduledAt: payload.scheduledAt,
        });
      } else {
        await createScheduledCohort(payload);
      }
      handleClose();
      fetchData();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await deleteScheduledCohort(deleteTarget.id);
      setDeleteTarget(null);
      fetchData();
    } finally {
      setSubmitting(false);
    }
  };

  const columnDefs = [
    {
      headerName: 'Cohort Name',
      field: 'name',
      filter: 'agTextColumnFilter',
      filterParams: { filterOptions: ['contains'], debounceMs: 200 },
      flex: 2,
    },
    {
      headerName: 'Course Type',
      field: 'courseType',
      filter: 'agTextColumnFilter',
      flex: 1,
      valueFormatter: (p) => COURSE_TYPE_SHORT_NAMES_MAPPING[p.value] || p.value || '—',
    },
    {
      headerName: 'Type',
      field: 'type',
      flex: 1,
    },
    {
      headerName: 'Start Date',
      field: 'startDate',
      flex: 1,
      valueFormatter: (p) => p.value ? moment(p.value).format('DD MMM YYYY, HH:mm') : '—',
    },
    {
      headerName: 'Scheduled At',
      field: 'scheduledAt',
      flex: 1,
      valueFormatter: (p) => p.value ? moment(p.value).format('DD MMM YYYY, HH:mm') : '—',
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
      cellRenderer: (p) => <StatusBadge value={p.value} />,
    },
    {
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (params) => {
        const isCreated = params.data?.status === 'created';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
            <button
              onClick={() => openView(params.data)}
              title="View"
              style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, color: '#6B7280' }}
            >
              <MdVisibility size={14} />
            </button>
            {!isCreated && (
              <>
                <button
                  onClick={() => openEdit(params.data)}
                  title="Edit"
                  style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, color: '#4F46E5' }}
                >
                  <MdEdit size={14} />
                </button>
                <button
                  onClick={() => setDeleteTarget(params.data)}
                  title="Delete"
                  style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, color: '#EF4444' }}
                >
                  <MdDelete size={14} />
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const isFormValid = form.name && form.courseType && form.startDate && form.classStartDate && form.scheduledAt;
  const modalTitle = isView ? 'View Scheduled Cohort' : editTarget ? 'Edit Scheduled Cohort' : 'New Scheduled Cohort';
  const modalSubtitle = isView
    ? 'Cohort details (read-only)'
    : editTarget
    ? 'Update cohort name and dates'
    : 'Fill in both sections to create a scheduled cohort';

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <div>
        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
              Scheduled Cohorts
            </h1>
            <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4, marginBottom: 0 }}>
              Manage cohorts that are scheduled for future start
            </p>
          </div>
          <button
            onClick={openCreate}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#4F46E5',
              border: 'none',
              borderRadius: 4,
              padding: '8px 16px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <HiOutlinePlus size={14} /> New Scheduled Cohort
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', color: '#374151', background: '#fff', cursor: 'pointer' }}
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <select
            value={filterCourseType}
            onChange={(e) => setFilterCourseType(e.target.value)}
            style={{ fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', color: '#374151', background: '#fff', cursor: 'pointer' }}
          >
            <option value="">All Course Types</option>
            {COURSE_TYPE_OPTIONS.map((ct) => (
              <option key={ct} value={ct}>{COURSE_TYPE_SHORT_NAMES_MAPPING[ct] || ct}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 6px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderBottom: '1px solid #F3F4F6' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Scheduled Cohorts</span>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: '#6B7280', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 9999, padding: '2px 10px' }}>
              {rows.length} records
            </span>
          </div>
          <div className="ag-theme-alpine" style={{ height: 520, width: '100%' }}>
            <AgGridReact
              columnDefs={columnDefs}
              defaultColDef={{ ...styledDefaultColDef, filter: true, flex: 1 }}
              rowData={rows}
              pagination
              paginationPageSize={10}
              headerHeight={40}
              rowHeight={52}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              onGridReady={(p) => p.api.sizeColumnsToFit()}
              onGridSizeChanged={(p) => p.api.sizeColumnsToFit()}
              overlayNoRowsTemplate='<span style="font-size:13px;color:#6B7280">No scheduled cohorts found</span>'
            />
          </div>
        </div>

        {/* Create / Edit Modal */}
        <Dialog
          open={modalOpen}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          PaperProps={{ style: { borderRadius: 16, overflow: 'hidden', maxWidth: 700 } }}
        >
          <DialogContent style={{ padding: 0 }}>
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #F3F4F6' }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>
                  {modalTitle}
                </h2>
                <p style={{ fontSize: 12, color: '#9CA3AF', margin: '2px 0 0' }}>
                  {modalSubtitle}
                </p>
              </div>
              <button
                onClick={handleClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: '#9CA3AF', borderRadius: 4 }}
              >
                <MdClose size={18} />
              </button>
            </div>

            {/* Two-panel body */}
            <div style={{ display: 'flex', minHeight: 260 }}>
              {/* Left panel: Cohort Info */}
              <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SectionLabel>Cohort Info</SectionLabel>

                <FormField label="Cohort Name" required={!isView}>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Cohort DS2512ABP"
                    disabled={isView}
                    style={inputStyle(isView)}
                  />
                </FormField>

                <FormField label="Course Type" required={!isView}>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={form.courseType}
                      onChange={(e) => setForm((p) => ({ ...p, courseType: e.target.value }))}
                      disabled={isView || !!editTarget}
                      style={{ ...inputStyle(isView || !!editTarget), appearance: 'none', paddingRight: 32, cursor: (isView || editTarget) ? 'default' : 'pointer' }}
                    >
                      {Object.values(COURSE_TYPE).map((ct) => (
                        <option key={ct} value={ct}>{COURSE_TYPE_SHORT_NAMES_MAPPING[ct] || ct}</option>
                      ))}
                    </select>
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9CA3AF', fontSize: 12 }}>▾</span>
                  </div>
                </FormField>

                <FormField label="Type" required={!isView}>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={form.type}
                      onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                      disabled={isView || !!editTarget}
                      style={{ ...inputStyle(isView || !!editTarget), appearance: 'none', paddingRight: 32, cursor: (isView || editTarget) ? 'default' : 'pointer' }}
                    >
                      <option value="learning">Learning</option>
                      <option value="almax">AlmaX</option>
                    </select>
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9CA3AF', fontSize: 12 }}>▾</span>
                  </div>
                </FormField>
              </div>

              {/* Vertical divider */}
              <div style={{ width: 1, backgroundColor: '#F3F4F6', flexShrink: 0 }} />

              {/* Right panel: Dates */}
              <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, backgroundColor: '#FAFAFA' }}>
                <SectionLabel>Dates</SectionLabel>

                <FormField label="Start Date" required={!isView}>
                  <KeyboardDateTimePicker
                    variant="inline"
                    inputVariant="outlined"
                    format="dd/MM/yyyy HH:mm"
                    placeholder="DD/MM/YYYY HH:mm"
                    value={form.startDate}
                    onChange={(date) => setForm((p) => ({ ...p, startDate: date }))}
                    size="small"
                    ampm={false}
                    disabled={isView}
                    InputProps={{ style: { fontSize: 13, fontFamily: "'Gellix','Inter',sans-serif" } }}
                    style={{ width: '100%' }}
                  />
                </FormField>

                <FormField label="Class Start Date" required={!isView}>
                  <KeyboardDateTimePicker
                    variant="inline"
                    inputVariant="outlined"
                    format="dd/MM/yyyy HH:mm"
                    placeholder="DD/MM/YYYY HH:mm"
                    value={form.classStartDate}
                    onChange={(date) => setForm((p) => ({ ...p, classStartDate: date }))}
                    size="small"
                    ampm={false}
                    disabled={isView}
                    InputProps={{ style: { fontSize: 13, fontFamily: "'Gellix','Inter',sans-serif" } }}
                    style={{ width: '100%' }}
                  />
                </FormField>

                <FormField label="Scheduled At" required={!isView}>
                  <KeyboardDateTimePicker
                    variant="inline"
                    inputVariant="outlined"
                    format="dd/MM/yyyy HH:mm"
                    placeholder="DD/MM/YYYY HH:mm"
                    value={form.scheduledAt}
                    onChange={(date) => setForm((p) => ({ ...p, scheduledAt: date }))}
                    size="small"
                    ampm={false}
                    disabled={isView}
                    InputProps={{ style: { fontSize: 13, fontFamily: "'Gellix','Inter',sans-serif" } }}
                    style={{ width: '100%' }}
                  />
                </FormField>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px', borderTop: '1px solid #F3F4F6', backgroundColor: '#fff' }}>
              {isView ? (
                <button
                  onClick={handleClose}
                  style={{ fontSize: 13, fontWeight: 600, color: '#fff', backgroundColor: '#4F46E5', border: 'none', borderRadius: 4, padding: '8px 20px', cursor: 'pointer' }}
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSubmit}
                    disabled={!isFormValid || submitting}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#4F46E5',
                      border: 'none',
                      borderRadius: 4,
                      padding: '8px 20px',
                      cursor: !isFormValid || submitting ? 'not-allowed' : 'pointer',
                      opacity: !isFormValid || submitting ? 0.6 : 1,
                      boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                      transition: 'opacity 150ms',
                    }}
                  >
                    {submitting ? 'Saving…' : editTarget ? 'Update Cohort' : 'Create Cohort'}
                  </button>
                  <button
                    onClick={handleReset}
                    style={{ fontSize: 13, fontWeight: 500, color: '#6B7280', backgroundColor: 'transparent', border: '1px solid #E5E7EB', borderRadius: 4, padding: '8px 16px', cursor: 'pointer' }}
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm Modal */}
        <Dialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
        >
          <DialogContent style={{ padding: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #F3F4F6' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Delete Scheduled Cohort</h2>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: '#9CA3AF', borderRadius: 4 }}
              >
                <MdClose size={18} />
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 24px' }}>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={submitting}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: '#EF4444',
                    border: 'none',
                    borderRadius: 4,
                    padding: '8px 20px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1,
                    transition: 'opacity 150ms',
                  }}
                >
                  {submitting ? 'Deleting…' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteTarget(null)}
                  style={{ fontSize: 13, fontWeight: 500, color: '#6B7280', backgroundColor: 'transparent', border: '1px solid #E5E7EB', borderRadius: 4, padding: '8px 16px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MuiPickersUtilsProvider>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{ fontSize: 10.5, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 12px 0' }}>
      {children}
    </p>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: '#374151', display: 'flex', gap: 3 }}>
        {label}
        {required && <span style={{ color: '#EF4444', lineHeight: 1 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function inputStyle(disabled) {
  return {
    width: '100%',
    height: 36,
    backgroundColor: disabled ? '#F9FAFB' : '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    padding: '0 10px',
    fontSize: 13,
    color: disabled ? '#9CA3AF' : '#111827',
    boxSizing: 'border-box',
    fontFamily: "'Gellix', 'Inter', sans-serif",
    transition: 'border-color 150ms',
  };
}
